import type { FileWithId } from 'src/types';

import { useBoolean } from 'usehooks-ts';
import React, { useMemo, useState, useCallback, createContext } from 'react';

// File Context
type TFileContext = {
  file: File | null | undefined;
  setFile: React.Dispatch<React.SetStateAction<TFileContext['file']>>;
};

const FileContext = createContext<TFileContext | undefined>(undefined);

// Message Context
type TMessageContext = {
  message: {
    value: string;
    files: FileWithId[];
    allFiles?: FileWithId[];
  };
  setMessage: React.Dispatch<React.SetStateAction<TMessageContext['message']>>;
  fileBrowserDialog: {
    value: boolean;
    setValue: React.Dispatch<React.SetStateAction<boolean>>;
    setTrue: () => void;
    setFalse: () => void;
    toggle: () => void;
  };
  isTranscribing: boolean;
  setIsTranscribing: React.Dispatch<React.SetStateAction<boolean>>;
  maxFileSize: number;
  setMaxFileSize: React.Dispatch<React.SetStateAction<number>>;
};

const MessageContext = createContext<TMessageContext | undefined>(undefined);

// Reset Context
type TResetContext = {
  reset: VoidFunction;
};

const ResetContext = createContext<TResetContext | undefined>(undefined);

// Combined Provider
type TCombinedProviderProps = {
  children: React.ReactNode;
};

// Create a combined provider to avoid re-rendering for components that doesn't need to listen to all contexts
const FileMessageProvider: React.FC<TCombinedProviderProps> = ({ children }) => {
  const [file, setFile] = useState<TFileContext['file']>(undefined);
  const [message, setMessage] = useState<{
    value: string;
    files: FileWithId[];
    allFiles?: FileWithId[];
  }>({
    value: '',
    files: [],
  });
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [maxFileSize, setMaxFileSize] = useState<number>(0);
  
  
  const fileBrowserDialog = useBoolean();

  const reset = useCallback(() => {
    setFile(undefined);
    setMessage({
      value: '',
      files: [],
      allFiles: [],
    });
  }, []);

  const fileContextValue = useMemo(() => ({ file, setFile }), [file]);
  const messageContextValue = useMemo(() => ({ message, setMessage, fileBrowserDialog, isTranscribing, setIsTranscribing, maxFileSize, setMaxFileSize }), [message, fileBrowserDialog, isTranscribing, maxFileSize]);
  const resetContextValue = useMemo(() => ({ reset }), [reset]);

  return (
    <ResetContext.Provider value={resetContextValue}>
      <FileContext.Provider value={fileContextValue}>
        <MessageContext.Provider value={messageContextValue}>{children}</MessageContext.Provider>
      </FileContext.Provider>
    </ResetContext.Provider>
  );
};

export { FileContext, ResetContext, MessageContext, FileMessageProvider };
